import { Page } from '@/declarations/models/Page';

/**
 * The pages last created, edited or moved, per site, so the page tree can open the way down to them and mark them.
 * Kept for the browser tab (sessionStorage), since some links to the editor load the app anew; without storage they
 * live in memory.
 */
const storageKey = (siteId: number) => `m24.lastPages.${siteId}`;
const inMemory = new Map<number, Array<number>>();

export const rememberLastPages = (siteId: number, pageIds: Array<number>) => {
  inMemory.set(siteId, pageIds);
  try {
    sessionStorage.setItem(storageKey(siteId), pageIds.join(','));
  } catch {
    // Memory is enough
  }
};

export const rememberLastPage = (siteId: number, pageId: number) => rememberLastPages(siteId, [pageId]);

/** Forgets them, once the tree has been opened or closed by hand: coming back shows the tree as it first is. */
export const forgetLastPages = (siteId: number) => {
  inMemory.delete(siteId);
  try {
    sessionStorage.removeItem(storageKey(siteId));
  } catch {
    // Memory is enough
  }
};

export const getLastPages = (siteId: number): Array<number> => {
  try {
    const stored = sessionStorage.getItem(storageKey(siteId));
    if (stored) return stored.split(',').map(Number).filter(Boolean);
  } catch {
    // Fall back to memory
  }
  return inMemory.get(siteId) ?? [];
};

/** The ids of the page's ancestors among the pages, nearest first; stops at a parent not in the list. */
export const ancestorIds = (pages: Array<Page>, pageId: number): Array<string> => {
  const parentOf = new Map(pages.map((page) => [page.id, page.parent_id]));
  const ids: Array<string> = [];
  let parentId = parentOf.get(pageId);
  while (parentId && parentOf.has(parentId) && !ids.includes(String(parentId))) {
    ids.push(String(parentId));
    parentId = parentOf.get(parentId);
  }
  return ids;
};
